import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import {
	ConfigurationFieldSchemaDto,
	InstitutionDownloadConfigDto,
} from "Infrastructure/Api/Api";
import { useEffect, useState } from "react";
import { useUpdateDownloadConfigMutation } from "../../Api/Mutations/useUpdateDownloadConfigMutation";

type Props = {
	config: InstitutionDownloadConfigDto | null;
	open: boolean;
	onClose: () => void;
};

const SENSITIVE_MASK = "••••••••";

export const DownloadConfigDialog: React.FC<Props> = ({
	config,
	open,
	onClose,
}) => {
	const [formValues, setFormValues] = useState<Record<string, string>>({});
	const [downloaderName, setDownloaderName] = useState("");

	const updateMutation = useUpdateDownloadConfigMutation();

	useEffect(() => {
		if (config) {
			setDownloaderName(config.downloaderName);
			setFormValues(config.configuration || {});
		}
	}, [config]);

	const handleFieldChange = (key: string, value: string) => {
		setFormValues((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const handleSave = () => {
		if (!config) return;

		updateMutation.mutate(
			{
				id: config.institutionDownloadConfigID,
				data: {
					downloaderName:
						downloaderName !== config.downloaderName
							? downloaderName
							: undefined,
					configuration: formValues,
					clearConfiguration: false,
				},
			},
			{
				onSuccess: (response) => {
					if (response.status === 200) {
						onClose();
					}
				},
			}
		);
	};

	const schema = config?.configurationSchema || [];

	const renderField = (field: ConfigurationFieldSchemaDto) => {
		const value = formValues[field.key] || "";
		const isMasked = field.sensitive && value === SENSITIVE_MASK;

		return (
			<TextField
				key={field.key}
				name={`config-${field.key}-${config?.institutionCode}`}
				label={field.label}
				type={field.sensitive ? "password" : "text"}
				required={field.required}
				helperText={field.description}
				value={value}
				onChange={(e) => handleFieldChange(field.key, e.target.value)}
				onFocus={() => {
					if (isMasked) {
						handleFieldChange(field.key, "");
					}
				}}
				fullWidth
				size="small"
				autoComplete={field.sensitive ? "new-password" : "off"}
				InputProps={
					field.sensitive
						? {
								endAdornment: (
									<InputAdornment position="end">
										<LockIcon fontSize="small" color="action" />
									</InputAdornment>
								),
							}
						: undefined
				}
			/>
		);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography variant="h6">Konfigurace stahování</Typography>
						{config && (
							<Chip
								size="small"
								label={config.institutionCode}
								variant="outlined"
							/>
						)}
					</Stack>
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent dividers>
			<form autoComplete="off">
				<Stack spacing={2} sx={{ pt: 1 }}>
					<TextField
						label="Název"
						name="downloader-display-name"
						value={downloaderName}
						onChange={(e) => setDownloaderName(e.target.value)}
						fullWidth
						size="small"
						autoComplete="off"
					/>

					{schema.length > 0 && (
						<>
							<Typography
								variant="subtitle2"
								color="text.secondary"
								sx={{ mt: 1 }}
							>
								Nastavení připojení
							</Typography>
							{schema.map(renderField)}
						</>
					)}

					{schema.length === 0 && (
						<Typography color="text.secondary" sx={{ py: 2 }}>
							Tato pojišťovna nemá žádnou konfiguraci k nastavení.
						</Typography>
					)}
				</Stack>
			</form>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={onClose}>
					Zrušit
				</Button>
				<Button
					variant="contained"
					onClick={handleSave}
					disabled={updateMutation.isPending}
				>
					Uložit
				</Button>
			</DialogActions>
		</Dialog>
	);
};
